<template>
  <v-container id="dashboard" fluid tag="section">
    <!-- OPTIONS & MANAGEMENT WIDGETS -->
    <v-row v-if="userLevelAboveAdmin">
      
      <v-col cols="12">
        <v-card class="my-0">
          <v-card-title>請先選擇點數/票券業者</v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12" md="2" class="pa-0">
                  <v-radio-group row v-model="form.merchantType">
                    <v-radio :label="$t('vca.points')" value="points"></v-radio>
                    <v-radio :label="$t('vca.tickets')" value="tickets"></v-radio>
                  </v-radio-group>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="form.merchantId"
                    :items="merchantOptions"
                    label="點數/票券業者"
                    placeholder="請先選擇點數/票券業者"
                    item-text="name"
                    item-value="merchantId"
                    dense
                    outlined
                    clearable
                  ></v-select>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>   
        </v-card>
        <v-card class="my-0">
          <v-card-title>
            進階搜尋
            <v-spacer></v-spacer>
            <v-btn text @click.stop="showExpand = !showExpand">
              <v-icon> {{ showExpand ? "mdi-chevron-up" : "mdi-chevron-down"}} </v-icon>
            </v-btn>
          </v-card-title>
            <v-container>            
              <v-expand-transition>
                <div v-show="showExpand">
                  <v-row>
                    <v-col cols="12" md="6"
                      lg="3">
                      <DateRangePicker
                        @update="onDateRangeUpdated"
                        :def-dates="dates"
                        :show-clear-btn="true"
                      >
                      </DateRangePicker>
                    </v-col>
                  </v-row>
                  <v-row>
                    <!-- 操作選單 -->
                    <v-col
                      v-for="config in filters"
                      :key="config.key"
                      cols="12"
                      md="6"
                      lg="3"
                    >
                    
                      <v-text-field
                        v-if="config.type === 'text'"
                        dense
                        outlined
                        v-model="form.search[config.key]"
                        :placeholder="config.placeholder"
                        clearable
                      >
                      </v-text-field>
                      <v-select
                        v-else-if="config.type === 'select'"
                        v-model="form.search[config.key]"
                        :items="config.options"
                        :label="config.label"
                        :placeholder="config.placeholder"
                        item-text="label"
                        item-value="value"
                        dense
                        outlined
                        clearable
                      ></v-select>
                    </v-col>
                  </v-row>
                </div>
              </v-expand-transition>
            </v-container>
          </v-card-text>
          <v-card-actions class="d-flex justify-end">
            <v-btn @click="exportVca" color="warning">
              <v-icon left>mdi-download</v-icon>
              {{ $t('vca.exportButton') }}
            </v-btn>
                
            <v-btn @click="showImportDialog"  color="blue darken-3">
              <v-icon left>mdi-upload</v-icon>
              {{ $t('vca.importButton') }}
            </v-btn>
        </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- LIST OF VCA -->
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-text>
            <SSRTable
              ref="vca-list"
              :data-url="loadUrl"
              :data-key="'data'"
              :schema="schema"
            >
              <div slot="createdAt" slot-scope="{ item }">
                {{ formatDate(item.createdAt) }}
              </div>
              <div slot="transactionTs" slot-scope="{ item }">
                {{ formatDate(item.transactionTs) }}
              </div>
              <div slot="merchantId" slot-scope="{ item }">
                {{ getMerchantText(item.merchantId)}}
              </div>
              <div slot="status" slot-scope="{ item }">
                {{ getFilterOptionText("status", item.status) }}
              </div>
              <div slot="actions" slot-scope="{ item }">
                <v-btn v-if="hasAuthoriztion" rounded depressed small color="primary" class="ml-1"
                  :disabled="item.status!=='failed'"
                  @click="resendVcaTransaction(item)">
                  {{ $t('vca.retry') }}
                </v-btn>

                <v-btn rounded depressed small color="warning" class="ml-1"
                  @click="showTransactionHistory(item)">
                  {{ $t('vca.history') }}
                </v-btn>
              </div>
            </SSRTable>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Dialog - Batch creation -->
    <UploadPreviewDialog :title="uploadDialogInfo.title" :show.sync="dialog.upload.show"
      :subtitle="uploadDialogInfo.subtitle"
      :sample-url="uploadDialogInfo.sampleUrl"
      success-message="Successfully submit batch."
      fail-message="Failed to submit batch."
      :use-form-data="uploadDialogInfo.formData"
      @submit="uploadDialogInfo.submit">
    </UploadPreviewDialog>

    <!-- Dialog - History -->
    <HistoryDialog :show.sync="dialog.history.show"
      :vca-id="dialog.history.vcaId"
      :vca-type="dialog.history.vcaType"  
    >
    </HistoryDialog>
  </v-container>
</template>

<script>
import SSRTable from "@/components/SSRTable";
import DateRangePicker from "@/components/DateRangePicker";
import UploadPreviewDialog from "@/components/UploadPreviewDialog";
import HistoryDialog from "./HistoryDialog";

import axios from "axios";
import moment from "moment";

export default {
  components: {
    SSRTable,
    DateRangePicker,
    UploadPreviewDialog,
    HistoryDialog
  },

  mounted() {
    this.fetchMerchantList();
  },

  methods: {
    onDateRangeUpdated(dates) {
      this.dates = dates;
    },
    formatDate(ts) {
      return new Date(ts * 1000).toLocaleString();
    },
    async fetchMerchantList() {
      try {
        let url = `${process.env.VUE_APP_QRPAY_ADMIN_HOST}/vca/${this.form.merchantType}/merchants`
        let response = await axios.get(url);
        this.merchantOptions = response.data;
      } catch (err) {
        console.error(`Failed to fetch ${this.form.merchantType} merchants`, err);
        throw err;
      }
    },
    getFilterOptionText(key, value) {
      const config = this.filters.find(cfg => cfg.key === key);
      if (config) {
        const item = config?.options.find(opt => opt.value === value);
        return item ? item.label : value;
      }
      return value;
    },

    getMerchantText(merchantId) {
      const merchant = this.merchantOptions.find(item => item.merchantId === merchantId);
      if (merchant) {
        return merchant.name || merchantId;
      }
      return merchantId;
    },
  
    exportVca() {
      if (!this.form.merchantId) {
        this.$swal({
          text: this.$t('vca.merchantIdIsNecessary'),
          icon: 'warning',
          confirmButtonText: this.$t('vca.confirm'),
          showCancelButton: false,
          showCloseButton: false
        });
        return; 
      }
      let url = `${process.env.VUE_APP_QRPAY_ADMIN_HOST}/vca/${this.form.merchantType}/export-excel?merchantId=${this.form.merchantId}` +
        (this.selectedParamsString
          ? `&${this.selectedParamsString}`
          : "")
      try {
        // window.open(url);
        axios.get(url)
        .then((res) => {
          this.$swal({
            text: res.data.message || this.$t('vca.exportReportSuccessMessage'),
            icon: 'info',
            confirmButtonText: '確認',
            showCancelButton: false,
            showCloseButton: false
          });
        })
        .catch((e) => {
          // console.log('e', e.response.data)
          let errMessage = e.response?.data?.error || e.stack;
          this.$notify({
            text: `Failed to donwload file. ${errMessage}`,
            type: 'error'
          });
        });
        
      } catch (e) {
        this.$notify({
          text: `Failed to donwload file. ${e.stack}`,
          type: 'error'
        });
      }
      
    },

    showImportDialog() {
      this.dialog.upload.show = true;
    },

    batchUpdate(data) {//'data' should be a file.
      if (!confirm(`About to update ${this.$t(`vca.${this.form.merchantType}`)} VCA status. Are you sure?`))
        return;

      // let formData = new FormData();
      // formData.append('file', data)
      console.log(
        'upload file:', data
      )
      axios( {
        method: "put",
        url: `${process.env.VUE_APP_QRPAY_ADMIN_HOST}/vca/${this.form.merchantType}/vca-status/batch`,
        data: {
          updates: data
        }
        // data: formData,
        // headers: { "Content-Type": "multipart/form-data" },
      })
      .then(response => {
        console.log(`[VCA] batch update succeeded`, response.data);
        this.$notify({
          text: `Successfully submitted batch.`,
          type: 'success'
        });
        this.$refs['vca-list'].loadData();
      })
      .catch(err => {
        console.error(`[VCA] batch update failed`, err);
        let errorMessage = `Failed to do batch update VCA status.`;
        if (err.response && err.response.data && err.response.data.message) {
          errorMessage +=  ` ${err.response.data.message}` ;
        }
        this.$notify({
          text: errorMessage,
          type: 'error'
        });
      });
    },

    resendVcaTransaction(item) {
      if (this.isProgressing) {
        this.$swal({
          text: this.$t('vca.progressing'),
          icon: 'warning',
          confirmButtonText: this.$t('vca.confirm'),
          showCancelButton: false,
          showCloseButton: false
        })
        return; 
      }
      this.$swal({
        text: this.$t('vca.retryConfirmMessage'),
        icon: 'warning',
        confirmButtonText: this.$t('vca.confirm'),
        showCancelButton: true,
        cancelButtonText: this.$t('vca.cancel'),
        showCloseButton: false
      })
      .then((result) => {
        if(result.isConfirmed) {
          this.resend(item);
        }
      })
    },
    resend(item) {
      this.isProgressing = true;
      let url = `${process.env.VUE_APP_QRPAY_ADMIN_HOST}/vca/${this.form.merchantType}/resend-vca`;
      try {
        // window.open(url);
        axios.post(
          url,
          {
            "merchantId": item.merchantId, 
            "merchantOrderId": item.merchantOrderId
          }
        )
        .then((res) => {
          this.$notify({
            text: this.$t('vca.retrySuccessMessage'),
            type: 'success'
          });
          this.$refs['vca-list'].loadData();
        })
        .catch((e) => {
          // console.log('e', e.response.data)
          let errMessage = e.response?.data?.error || e.stack;
          this.$notify({
            text: `Failed to resend VCA transaction. ${errMessage}`,
            type: 'error'
          });
        })
        .finally(() => {
          this.isProgressing = false;
        });
        
      } catch (e) {
        this.$notify({
          text: `Failed to resend VCA transaction. ${e.stack}`,
          type: 'error'
        });
      } 
      
    },
    showTransactionHistory(item) {
      this.dialog.history.vcaId = item.id.toString();
      this.dialog.history.vcaType = this.form.merchantType;
      this.dialog.history.show = true;
    }
  },

  data() {
    return {
      dates: [],
      scope: {},
      merchantOptions: [],
      showExpand: true,
      isProgressing: false, 
      form: {
        merchantType: "points", // 支援points/tickets
        merchantId: "",
        search: {
          searchQueryString: ""
        }
      },
      dialog: {
        upload: {
          show: false
        },
        history: {
          show: false,
          vcaId: null,
          vcaType: null,
        }
      }

    };
  },
  watch: {
   
  },

  computed: {
    hasAuthoriztion() {
      const allowed = process.env?.VUE_APP_VCA_RETRY_WHITELIST?.trim() ? process.env.VUE_APP_VCA_RETRY_WHITELIST?.trim().split(',') : []; 
      const currentUserEmail = this.currentUser.email;
      if (allowed?.length && allowed.includes(currentUserEmail)) {
        return true;
      }
      return false; 
    },
    currentUser() {
      return this.$store.getters.user;
    },
    filters () {
      const options = [{
        "key": "status",
        "type": "select",
        "label": this.$t('vca.status'),
        "placeholder": "請選擇狀態",
        "options": [
          {
              "value": "failed",
              "label": "失敗"
          },
          {
              "value": "completed",
              "label": "完成"
          },
          {
              "value": "pending",
              "label": "未處理"
          },
          {
              "value": "processing",
              "label": "處理中"
          },
          {
              "value": "void",
              "label": "取消"
          },
          {
              "value": "refunded",
              "label": "已退款"
          },
          {
              "value": "manual",
              "label": "已人工處理"
          }
        ]
      },{
        "key": "shopId",
        "type": "text",
        "label": this.$t('vca.shopId'),
        "placeholder": "請輸入特店ID",
      },{
        "key": "merchantOrderId",
        "type": "text",
        "label": this.$t('vca.merchantOrderId'),
        "placeholder": `請輸入${this.$t('vca.merchantOrderId')}`,
      }];
      
      // 初始化 form.search 的欄位
      options.forEach(config => {
        this.$set(this.form.search, config.key, null);
      });
      return options;
    },
    selectedParamsString() {
      const entries = Object.entries(this.form.search)
        .filter(([_, value]) => value !== null && value !== "") // 忽略未選取的
        .map(([key, value]) => `${key}=${value}`);

      let paramsString = entries.join("&");
      let from = this.dates.length
        ? moment(this.dates[0]).unix()
        : null;
      let to =
        this.dates.length > 1
          ? moment(this.dates[1])
              .endOf("day")
              .unix()
          : null;
      if (from && to) {
        paramsString += paramsString ? `&from=${from}&to=${to}` : `from=${from}&to=${to}`;
      }

      return paramsString;
   
    },
    loadUrl() {
      if (!this.form.merchantType || !this.form.merchantId) return;
      
      return (
        `${process.env.VUE_APP_QRPAY_ADMIN_HOST}/vca/${this.form.merchantType}?merchantId=${this.form.merchantId}` +
        (this.selectedParamsString
          ? `&${this.selectedParamsString}`
          : "")
      );
    },

    schema() {
      let pointSchema = [
        {
          text: this.$t("vca.merchantId"),
          value: "merchantId",
          sortable: false
        },
        { text: this.$t("vca.status"), value: "status", sortable: false },
        { text: this.$t("vca.shopId"), value: "shopId", sortable: false },
        { text: this.$t("vca.storeName"), value: "storeName", sortable: false },
        { text: this.$t("vca.merchantOrderId"), value: "merchantOrderId", sortable: false },
        { text: this.$t("vca.ddpgOrderId"), value: "ddpgOrderId", sortable: false },
        { text: this.$t("vca.amount"), value: "amount", sortable: false },
        { text: this.$t("vca.points"), value: "points", sortable: false },
        {
          text: this.$t("vca.createdAt"),
          value: "createdAt",
          sortable: false
        },
         {
          text: this.$t("vca.transactionTs"),
          value: "transactionTs",
          sortable: false
        },
        {
          text: this.$t("vca.settlementDate"),
          value: "settlementDate",
          sortable: false
        },
      ];

      let ticketSchema = [
        {
          text: this.$t("vca.merchantId"),
          value: "merchantId",
          sortable: false
        },
        { text: this.$t("vca.shopId"), value: "shopId", sortable: false },
        { text: this.$t("vca.status"), value: "status", sortable: false },
        { text: this.$t("vca.merchantOrderId"), value: "merchantOrderId", sortable: false },
        { text: this.$t("vca.ddpgOrderId"), value: "ddpgOrderId", sortable: false },
        { text: this.$t("vca.ticket.provider"), value: "provider", sortable: false },
        { text: this.$t("vca.ticket.ticketId"), value: "ticketId", sortable: false },
        { text: this.$t("vca.ticket.amount"), value: "amount", sortable: false },
        { text: this.$t("vca.ticket.originalAmount"), value: "originalAmount", sortable: false },
        { text: this.$t("vca.ticket.discount"), value: "discount", sortable: false },
        {text: this.$t("vca.createdAt"),
          value: "createdAt",
          sortable: false
        },
        {
          text: this.$t("vca.transactionTs"),
          value: "transactionTs",
          sortable: false
        },
        {
          text: this.$t("vca.settlementDate"),
          value: "settlementDate",
          sortable: false
        }
      ];

      let selected = this.form.merchantType === 'tickets' ? ticketSchema : pointSchema;

      if (this.isRetryFeatureOpen) {
        selected.push({
          text: this.$t("vca.actions"),
          value: "actions",
          sortable: false
        })
      }
      // if (this.hasAuthoriztion) {
      //   selected.push({
      //     text: this.$t("vca.actions"),
      //     value: "actions",
      //     sortable: false
      //   })
      // }
      return selected;
    },
    isRetryFeatureOpen() {
      // 1. 取得開放日期 (openDate)
      const openDate = process.env.VUE_APP_VCA_RETRY_OPENDATE 
        ? moment(process.env.VUE_APP_VCA_RETRY_OPENDATE)
        : moment().add(1, 'd');
      
      // 2. 取得當前日期 (currentDate)
      const currentDate = moment();

      // 3. 進行比較並返回結果
      return currentDate.isSameOrAfter(openDate);
    },
    uploadDialogInfo() {
      return {
          sampleUrl: undefined,
          // url: this.batchUpdateUrl,
          title: `${this.$t('vca.importButton')} - ${this.$t(`vca.${this.form.merchantType}`)}`,
          subtitle: this.$t('vca.importDesc'),
          submit: this.batchUpdate,
          formData: false
        }
     
    },
    
    user() {
      return this.$store.getters.user;
    },
    userLevelAboveAdmin() {
      return ["superadmin"].indexOf(this.$store.getters.user["level"]) >= 0;
    }
  }
};

</script>

<style scoped>
.truncate-text {
  max-height: 3.6em; /* 約兩行文字高度 */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* 限制顯示行數 */
  -webkit-box-orient: vertical;
}
.expand-btn {
  background-color: white !important;
  color: black !important
}

</style>
